import type {
  AccessRule,
  AdminGameRow,
  CardImportResult,
  ContentKind,
  CreateDeckInput,
  CreateTrainingBlockInput,
  CreateTrainingMaterialInput,
  DeckDetail,
  DeckSummary,
  TrainingMaterialDetail,
  TrainingMaterialSummary,
  UpdateCardInput,
  UpdateDeckInput,
  UpdateTrainingBlockInput,
  UpdateTrainingMaterialInput,
} from '@creativitas/shared';
import { ApiError, apiFetch } from '@/lib/api';

export const ADMIN_DECKS_KEY = ['admin', 'decks'] as const;
export const ADMIN_TRAINING_KEY = ['admin', 'training'] as const;

/* --------------------------------- decks ---------------------------------- */

export const fetchAdminDecks = (): Promise<{ items: DeckSummary[] }> =>
  apiFetch<{ items: DeckSummary[] }>('/admin/decks');

export const fetchAdminDeck = (deckId: string): Promise<DeckDetail> =>
  apiFetch<DeckDetail>(`/admin/decks/${deckId}`);

export const createDeck = (input: CreateDeckInput): Promise<DeckDetail> =>
  apiFetch<DeckDetail>('/admin/decks', { method: 'POST', body: input });

export const updateDeck = (deckId: string, input: UpdateDeckInput): Promise<DeckDetail> =>
  apiFetch<DeckDetail>(`/admin/decks/${deckId}`, { method: 'PATCH', body: input });

export const deleteDeck = (deckId: string): Promise<void> =>
  apiFetch<void>(`/admin/decks/${deckId}`, { method: 'DELETE' });

export const reorderCards = (deckId: string, cardIds: string[]): Promise<DeckDetail> =>
  apiFetch<DeckDetail>(`/admin/decks/${deckId}/cards/order`, {
    method: 'PUT',
    body: { cardIds },
  });

export const updateCard = (cardId: string, input: UpdateCardInput): Promise<void> =>
  apiFetch<void>(`/admin/cards/${cardId}`, { method: 'PATCH', body: input });

export const deleteCard = (cardId: string): Promise<void> =>
  apiFetch<void>(`/admin/cards/${cardId}`, { method: 'DELETE' });

/* -------------------------------- uploads --------------------------------- */

/**
 * Multipart uploads bypass `apiFetch` because the browser must set its own
 * `Content-Type` boundary.
 */
async function postFiles<T>(path: string, files: File[]): Promise<T> {
  const form = new FormData();
  for (const file of files) form.append('files', file);

  const response = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'upload_failed',
      error?.message ?? 'Не удалось загрузить файлы',
    );
  }

  return payload as T;
}

export const importCards = (
  deckId: string,
  files: File[],
): Promise<CardImportResult & { deck: DeckDetail }> =>
  postFiles<CardImportResult & { deck: DeckDetail }>(`/admin/decks/${deckId}/cards`, files);

export const uploadImage = (file: File): Promise<{ url: string; bytes: number }> =>
  postFiles<{ url: string; bytes: number }>('/admin/uploads/image', [file]);

/* ------------------------------ access rules ------------------------------ */

export const setAccessRules = (
  kind: ContentKind,
  contentId: string,
  rules: AccessRule[],
  requiredGameId: string | null,
): Promise<{ rules: AccessRule[] }> =>
  apiFetch<{ rules: AccessRule[] }>(`/admin/access/${kind}/${contentId}`, {
    method: 'PUT',
    body: { rules, requiredGameId },
  });

/* -------------------------------- training -------------------------------- */

export const fetchAdminTraining = (): Promise<{ items: TrainingMaterialSummary[] }> =>
  apiFetch<{ items: TrainingMaterialSummary[] }>('/admin/training');

export const fetchAdminMaterial = (id: string): Promise<TrainingMaterialDetail> =>
  apiFetch<TrainingMaterialDetail>(`/admin/training/${id}`);

export const createMaterial = (
  input: CreateTrainingMaterialInput,
): Promise<TrainingMaterialDetail> =>
  apiFetch<TrainingMaterialDetail>('/admin/training', { method: 'POST', body: input });

export const updateMaterial = (
  id: string,
  input: UpdateTrainingMaterialInput,
): Promise<TrainingMaterialDetail> =>
  apiFetch<TrainingMaterialDetail>(`/admin/training/${id}`, { method: 'PATCH', body: input });

export const deleteMaterial = (id: string): Promise<void> =>
  apiFetch<void>(`/admin/training/${id}`, { method: 'DELETE' });

export const addBlock = (
  materialId: string,
  input: CreateTrainingBlockInput,
): Promise<TrainingMaterialDetail> =>
  apiFetch<TrainingMaterialDetail>(`/admin/training/${materialId}/blocks`, {
    method: 'POST',
    body: input,
  });

export const updateBlock = (blockId: string, input: UpdateTrainingBlockInput): Promise<void> =>
  apiFetch<void>(`/admin/training-blocks/${blockId}`, { method: 'PATCH', body: input });

export const deleteBlock = (blockId: string): Promise<void> =>
  apiFetch<void>(`/admin/training-blocks/${blockId}`, { method: 'DELETE' });

export const reorderBlocks = (
  materialId: string,
  blockIds: string[],
): Promise<TrainingMaterialDetail> =>
  apiFetch<TrainingMaterialDetail>(`/admin/training/${materialId}/blocks/order`, {
    method: 'PUT',
    body: { blockIds },
  });

/* --------------------------------- games ---------------------------------- */

export const ADMIN_GAMES_KEY = ['admin', 'games'] as const;

export const fetchAdminGames = (): Promise<{ items: AdminGameRow[] }> =>
  apiFetch<{ items: AdminGameRow[] }>('/admin/games');

export const setGameStatus = (gameId: string, status: 'draft' | 'published'): Promise<{ items: AdminGameRow[] }> =>
  apiFetch<{ items: AdminGameRow[] }>(`/admin/games/${gameId}/status`, { method: 'PUT', body: { status } });
